import React, { useEffect } from 'react';

import { DETAIL_CAST_CREW_ARROW } from 'config/testID';

import { ContentType } from 'services/data-manager/types';

import { isEmpty } from 'utils/object';
import { widthPercentageToDP } from 'utils/responsiveness';

import ArrowButton, { EArrowType } from 'components/CustomButtons/ArrowButton';
import {
    CAST_MARGIN_WEB,
    STRIPE_MARGIN_WEB,
} from 'components/CustomButtons/RibbonTouchable/styles';
import { getRibbonCardWidth } from 'components/Ribbon/utils';

interface IProps {
    visibleItems: Record<string, boolean>;
    itemsLength: number;
    maxItemsOnScreen: number;
    metadataType: ContentType;
    initialScrollIndex?: number | null;
    scrollOffset: number;
    snapOffsets: Array<number>;
    containerWidth: number;
    onPress: (type: EArrowType) => void;
    railId?: string;
}

const CONTAINER_PADDING = widthPercentageToDP(72);

export const RibbonArrows = ({
    visibleItems,
    itemsLength,
    maxItemsOnScreen,
    metadataType,
    initialScrollIndex,
    scrollOffset,
    snapOffsets,
    containerWidth,
    onPress,
    railId,
}: IProps) => {
    const visibleItemsArray = Object.values(visibleItems);
    const isChannelStripe = metadataType === ContentType.ChannelItem;
    const isPersonStripe = metadataType === ContentType.CastPerson;
    const itemWidth = getRibbonCardWidth(metadataType);
    const scrolledBackFromInitialIndex =
        initialScrollIndex !== 0 && scrollOffset === 0;

    const isFwdVisible =
        ((visibleItemsArray[0] && scrolledBackFromInitialIndex) ||
            scrollOffset <
                snapOffsets[snapOffsets.length - maxItemsOnScreen - 1] -
                    CONTAINER_PADDING * 2) &&
        itemsLength > maxItemsOnScreen;

    const isBwdVisible =
        !isEmpty(visibleItems) &&
        scrollOffset !== 0 &&
        (!visibleItemsArray[0] ||
            initialScrollIndex !== 0 ||
            scrollOffset >= itemWidth);

    const arrowWidth =
        containerWidth -
        maxItemsOnScreen * itemWidth -
        maxItemsOnScreen *
            (isPersonStripe ? CAST_MARGIN_WEB : STRIPE_MARGIN_WEB);
    const leftArrowWidth = isFwdVisible ? CONTAINER_PADDING : arrowWidth;

    // @ts-ignore
    const handleDetectRibbonHover = (event) => {
        const { left, width } = event.target.getBoundingClientRect();

        if (left <= leftArrowWidth + 20) {
            const bwdArrows = document.querySelectorAll(
                'div[data-class="arrow_bwd"]',
            );

            bwdArrows.forEach(
                (bwdArrow) =>
                    (bwdArrow.getAttribute('data-id') ===
                        event.target.getAttribute('data-id') ||
                        bwdArrow.getAttribute('data-id') ===
                            event.target.parentElement.getAttribute(
                                'data-id',
                            ) ||
                        bwdArrow.getAttribute('data-id') ===
                            event.target.parentElement.parentElement.getAttribute(
                                'data-id',
                            )) &&
                    bwdArrow?.classList.add('arrowHidden'),
            );
        } else if (width * (maxItemsOnScreen - 0.5) < left) {
            const fwdArrows = document.querySelectorAll(
                'div[data-class="arrow_fwd"]',
            );

            fwdArrows.forEach(
                (fwdArrow) =>
                    (fwdArrow.getAttribute('data-id') ===
                        event.target.getAttribute('data-id') ||
                        fwdArrow.getAttribute('data-id') ===
                            event.target.parentElement.getAttribute(
                                'data-id',
                            ) ||
                        fwdArrow.getAttribute('data-id') ===
                            event.target.parentElement.parentElement.getAttribute(
                                'data-id',
                            )) &&
                    fwdArrow?.classList.add('arrowHidden'),
            );
        }
    };

    // @ts-ignore
    const handleDetectRibbonMouseLeave = (event) => {
        const { left, width } = event.target.getBoundingClientRect();

        if (left <= leftArrowWidth + 20) {
            const bwdArrows = document.querySelectorAll(
                'div[data-class="arrow_bwd"]',
            );

            setTimeout(() => {
                bwdArrows.forEach(
                    (bwdArrow) =>
                        (bwdArrow.getAttribute('data-id') ===
                            event.target.getAttribute('data-id') ||
                            bwdArrow.getAttribute('data-id') ===
                                event.target.parentElement.getAttribute(
                                    'data-id',
                                ) ||
                            bwdArrow.getAttribute('data-id') ===
                                event.target.parentElement.parentElement.getAttribute(
                                    'data-id',
                                )) &&
                        bwdArrow?.classList.remove('arrowHidden'),
                );
            }, 250);
        } else if (width * (maxItemsOnScreen - 0.5) < left) {
            const fwdArrows = document.querySelectorAll(
                'div[data-class="arrow_fwd"]',
            );
            setTimeout(() => {
                fwdArrows.forEach(
                    (fwdArrow) =>
                        (fwdArrow.getAttribute('data-id') ===
                            event.target.getAttribute('data-id') ||
                            fwdArrow.getAttribute('data-id') ===
                                event.target.parentElement.getAttribute(
                                    'data-id',
                                ) ||
                            fwdArrow.getAttribute('data-id') ===
                                event.target.parentElement.parentElement.getAttribute(
                                    'data-id',
                                )) &&
                        fwdArrow?.classList.remove('arrowHidden'),
                );
            }, 250);
        }
    };

    useEffect(() => {
        const hoveredRibbons = document.querySelectorAll(
            'div[data-class="ribbonItemContent"]',
        );
        hoveredRibbons.forEach((hoveredRibbon) =>
            hoveredRibbon?.addEventListener(
                'mouseover',
                handleDetectRibbonHover,
            ),
        );
        hoveredRibbons.forEach((hoveredRibbon) =>
            hoveredRibbon?.addEventListener(
                'mouseleave',
                handleDetectRibbonMouseLeave,
            ),
        );

        return () => {
            hoveredRibbons.forEach((hoveredRibbon) =>
                hoveredRibbon?.removeEventListener(
                    'mouseover',
                    handleDetectRibbonHover,
                ),
            );
            hoveredRibbons.forEach((hoveredRibbon) =>
                hoveredRibbon?.removeEventListener(
                    'mouseleave',
                    handleDetectRibbonMouseLeave,
                ),
            );
        };
    });

    return (
        <>
            {isBwdVisible && (
                <ArrowButton
                    testID={`${DETAIL_CAST_CREW_ARROW}_BACKWOARD`}
                    type={EArrowType.BWD}
                    onArrowNavigationPress={onPress}
                    hasTitle={!isChannelStripe}
                    width={leftArrowWidth}
                    metadataType={metadataType}
                    railId={railId}
                />
            )}
            {isFwdVisible && (
                <ArrowButton
                    testID={`${DETAIL_CAST_CREW_ARROW}_FORWOARD`}
                    type={EArrowType.FWD}
                    onArrowNavigationPress={onPress}
                    hasTitle={metadataType !== ContentType.ChannelItem}
                    width={arrowWidth}
                    metadataType={metadataType}
                    railId={railId}
                />
            )}
        </>
    );
};
